import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Button } from '../ui/button';
import { DataTable } from '../ui/data-table';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/modal';
import { Input } from '../ui/input';
import { classFeedbackAPI, feedbackQuestionAPI, feedbackResponseAPI, feedbackDataAPI } from '../../services/api';

const ClassFeedback = () => {
  const { classId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get class data from navigation state
  const classData = location.state || {};
  const classTitle = classData.classTitle || 'Unknown Class';

  // State management
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  
  // Form states
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackQuestions, setFeedbackQuestions] = useState([]);
  const [feedbackResponses, setFeedbackResponses] = useState([]);
  const [completeFeedbackData, setCompleteFeedbackData] = useState(null);
  
  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [responsesLoading, setResponsesLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_active: false
  });

  const [questionFormData, setQuestionFormData] = useState({
    question_type: 'multiple_choice',
    question_text: '',
    options: ['']
  });

  useEffect(() => {
    fetchFeedbacks();
  }, [classId]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await classFeedbackAPI.getClassFeedbacksByClassId(classId);
      setFeedbacks(response.data || []);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      setError('Failed to load feedbacks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFeedback = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      const submitData = {
        class_id: parseInt(classId),
        title: formData.title,
        description: formData.description,
        is_active: formData.is_active ? 1 : 0
      };
      
      await classFeedbackAPI.createClassFeedback(submitData);
      
      // Clear cache to ensure data consistency
      classFeedbackAPI.clearClassFeedbackCache();
      
      setIsCreateModalOpen(false);
      resetForm();
      await fetchFeedbacks();
      
    } catch (error) {
      console.error('Error creating feedback:', error);
      alert('Failed to create feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewFeedback = async (feedback) => {
    try {
      setSelectedFeedback(feedback);
      setIsViewModalOpen(true);
      setQuestionsLoading(true);
      
      // Fetch questions for this feedback
      const questionsResponse = await feedbackQuestionAPI.getFeedbackQuestionsByFeedbackId(feedback.id);
      setFeedbackQuestions(questionsResponse.data || []);
      
      // Fetch complete feedback data
      const completeDataResponse = await feedbackDataAPI.getCompleteFeedbackData(feedback.id);
      setCompleteFeedbackData(completeDataResponse.data || null);
      
    } catch (error) {
      console.error('Error fetching feedback details:', error);
      setFeedbackQuestions([]);
      setCompleteFeedbackData(null);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleActivateFeedback = async (feedback) => {
    try {
      await classFeedbackAPI.activateClassFeedback(feedback.id);
      await fetchFeedbacks();
      alert('Feedback activated successfully!');
    } catch (error) {
      console.error('Error activating feedback:', error);
      alert('Failed to activate feedback. Please try again.');
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) {
      return;
    }

    try {
      await classFeedbackAPI.deleteClassFeedback(feedbackId);
      
      // Clear cache to ensure data consistency
      classFeedbackAPI.clearClassFeedbackCache();
      
      await fetchFeedbacks();
      alert('Feedback deleted successfully!');
    } catch (error) {
      console.error('Error deleting feedback:', error);
      alert('Failed to delete feedback. Please try again.');
    }
  };

  const handleAddQuestion = (feedback) => {
    setSelectedFeedback(feedback);
    setIsQuestionModalOpen(true);
    resetQuestionForm();
  };

  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      const submitData = {
        feedback_id: selectedFeedback.id,
        question_type: questionFormData.question_type,
        question_text: questionFormData.question_text,
        options: questionFormData.options.filter(option => option.trim() !== '')
      };
      
      await feedbackQuestionAPI.createFeedbackQuestion(submitData);
      
      // Clear cache to ensure data consistency
      feedbackQuestionAPI.clearFeedbackQuestionCache();
      
      setIsQuestionModalOpen(false);
      resetQuestionForm();
      alert('Question added successfully!');
      
    } catch (error) {
      console.error('Error creating question:', error);
      alert('Failed to create question. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewResponses = async (feedback) => {
    try {
      setSelectedFeedback(feedback);
      setIsResponseModalOpen(true);
      setResponsesLoading(true);
      
      const responsesResponse = await feedbackResponseAPI.getFeedbackResponsesByFeedbackId(feedback.id);
      setFeedbackResponses(responsesResponse.data || []);
      
    } catch (error) {
      console.error('Error fetching responses:', error);
      setFeedbackResponses([]);
    } finally {
      setResponsesLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleQuestionInputChange = (e) => {
    const { name, value } = e.target;
    setQuestionFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOptionChange = (index, value) => {
    setQuestionFormData(prev => ({
      ...prev,
      options: prev.options.map((option, i) => i === index ? value : option)
    }));
  };

  const addOption = () => {
    setQuestionFormData(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const removeOption = (index) => {
    setQuestionFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      is_active: false
    });
  };

  const resetQuestionForm = () => {
    setQuestionFormData({
      question_type: 'multiple_choice',
      question_text: '',
      options: ['']
    });
  };

  const handleGoBack = () => {
    navigate(`/classes/${classId}`);
  };

  const handleDownloadResponsesExcel = () => {
    if (!feedbackResponses.length) return;

    const rows = feedbackResponses.map((response, index) => ({
      '#': index + 1,
      'User ID': response.user_id,
      'Responses': typeof response.responses === 'object'
        ? JSON.stringify(response.responses)
        : response.responses,
      'Submitted At': response.created_at
        ? new Date(response.created_at).toLocaleString()
        : 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Responses');

    const fileName = `${selectedFeedback?.title || 'feedback'}_responses.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const feedbackColumns = [
    {
      accessorKey: 'id',
      header: 'ID',
      sortable: true
    },
    {
      accessorKey: 'title',
      header: 'Title',
      sortable: true
    },
    {
      accessorKey: 'description',
      header: 'Description',
      sortable: false,
      cell: (feedback) => (
        <div className="max-w-xs truncate" title={feedback.description}>
          {feedback.description || 'No description'}
        </div>
      )
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      sortable: true,
      cell: (feedback) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          feedback.is_active 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {feedback.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      sortable: true,
      cell: (feedback) => feedback.created_at ? new Date(feedback.created_at).toLocaleDateString() : 'N/A'
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      sortable: false,
      className: 'w-32',
      cell: (feedback) => (
        <div className="flex space-x-1">
          <button
            onClick={() => handleViewFeedback(feedback)}
            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
            title="View Details"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button
            onClick={() => handleAddQuestion(feedback)}
            className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
            title="Add Question"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
          <button
            onClick={() => handleViewResponses(feedback)}
            className="p-1.5 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-full transition-colors"
            title="View Responses"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </button>
          {!feedback.is_active && (
            <button
              onClick={() => handleActivateFeedback(feedback)}
              className="p-1.5 text-orange-500 hover:text-orange-700 hover:bg-orange-50 rounded-full transition-colors"
              title="Activate Feedback"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </button>
          )}
          <button
            onClick={() => handleDeleteFeedback(feedback.id)}
            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
            title="Delete Feedback"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <p className="mt-4 text-gray-600">Loading feedback data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6">
        <div className="text-center py-8">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={fetchFeedbacks} className="bg-purple-600 hover:bg-purple-700">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-9">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleGoBack}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              title="Back to class details"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Class Feedback</h1>
              <p className="text-lg text-gray-600">Manage feedback for {classTitle}</p>
            </div>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            + Create Feedback
          </button>
        </div>
      </div>

      <DataTable
        columns={feedbackColumns}
        data={feedbacks}
        searchKey="title"
        searchPlaceholder="Search feedback..."
        loading={loading}
        emptyMessage="No feedback found for this class."
      />

      {/* Create Feedback Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)}>
        <form onSubmit={handleCreateFeedback}>
          <ModalHeader>
            <h2 className="text-xl font-semibold">Create New Feedback</h2>
          </ModalHeader>

          <ModalBody className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <Input
                id="title"
                name="title"
                type="text"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter feedback title"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                placeholder="Enter feedback description"
              />
            </div>

            <div className="flex items-center">
              <input
                id="is_active"
                name="is_active"
                type="checkbox"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                Make this feedback active
              </label>
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              onClick={() => setIsCreateModalOpen(false)}
              className="mr-2 bg-gray-500 hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isSubmitting ? 'Creating...' : 'Create Feedback'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* View Feedback Modal */}
      <Modal 
        isOpen={isViewModalOpen} 
        onClose={() => setIsViewModalOpen(false)}
        className="max-w-4xl"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold">Feedback Details</h2>
          <p className="text-gray-600">{selectedFeedback?.title}</p>
        </ModalHeader>

        <ModalBody className="space-y-6 overflow-y-auto max-h-[70vh]">
          {selectedFeedback && (
            <>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-medium text-purple-900 mb-2">{selectedFeedback.title}</h3>
                <p className="text-purple-700 text-sm mb-3">{selectedFeedback.description}</p>
                <div className="flex items-center space-x-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    selectedFeedback.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedFeedback.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="text-sm text-gray-500">
                    Created: {selectedFeedback.created_at ? new Date(selectedFeedback.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Questions ({feedbackQuestions.length})</h4>
                {questionsLoading ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    <p className="mt-2 text-gray-600">Loading questions...</p>
                  </div>
                ) : feedbackQuestions.length > 0 ? (
                  <div className="space-y-3">
                    {feedbackQuestions.map((question, index) => (
                      <div key={question.id} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">
                              {index + 1}. {question.question_text}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              Type: {question.question_type?.replace('_', ' ') || 'Unknown'}
                            </p>
                            {question.options && question.options.length > 0 && (
                              <div className="mt-2">
                                <p className="text-sm text-gray-600 mb-1">Options:</p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-2">
                                  {question.options.map((option, optIndex) => (
                                    <li key={optIndex}>{option}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No questions added yet.</p>
                    <Button
                      onClick={() => {
                        setIsViewModalOpen(false);
                        handleAddQuestion(selectedFeedback);
                      }}
                      className="mt-2 bg-green-600 hover:bg-green-700"
                      size="sm"
                    >
                      Add First Question
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={() => setIsViewModalOpen(false)}
            className="mr-2 bg-gray-500 hover:bg-gray-600"
          >
            Close
          </Button>
          {selectedFeedback && (
            <Button
              onClick={() => {
                setIsViewModalOpen(false);
                handleAddQuestion(selectedFeedback);
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              Add Question
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Add Question Modal */}
      <Modal 
        isOpen={isQuestionModalOpen} 
        onClose={() => setIsQuestionModalOpen(false)}
        className="max-w-2xl"
      >
        <form onSubmit={handleCreateQuestion}>
          <ModalHeader>
            <h2 className="text-xl font-semibold">Add Question</h2>
            <p className="text-gray-600">Add a new question to: {selectedFeedback?.title}</p>
          </ModalHeader>

          <ModalBody className="space-y-4">
            <div>
              <label htmlFor="question_type" className="block text-sm font-medium text-gray-700 mb-1">
                Question Type *
              </label>
              <select
                id="question_type"
                name="question_type"
                value={questionFormData.question_type}
                onChange={handleQuestionInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="multiple_choice">Multiple Choice</option>
                <option value="rating">Rating Scale</option>
                <option value="text">Text Response</option>
                <option value="yes_no">Yes/No</option>
              </select>
            </div>

            <div>
              <label htmlFor="question_text" className="block text-sm font-medium text-gray-700 mb-1">
                Question Text *
              </label>
              <textarea
                id="question_text"
                name="question_text"
                value={questionFormData.question_text}
                onChange={handleQuestionInputChange}
                required
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                placeholder="Enter your question here..."
              />
            </div>

            {(questionFormData.question_type === 'multiple_choice' || questionFormData.question_type === 'rating') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Options *
                </label>
                <div className="space-y-2">
                  {questionFormData.options.map((option, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Input
                        type="text"
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1"
                      />
                      {questionFormData.options.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addOption}
                    className="text-sm text-purple-600 hover:text-purple-700 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Option
                  </button>
                </div>
              </div>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              onClick={() => setIsQuestionModalOpen(false)}
              className="mr-2 bg-gray-500 hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? 'Adding...' : 'Add Question'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* View Responses Modal */}
      <Modal 
        isOpen={isResponseModalOpen} 
        onClose={() => setIsResponseModalOpen(false)}
        className="max-w-4xl"
      >
        <ModalHeader>
          <div className="flex items-center justify-between w-full">
            <div>
              <h2 className="text-xl font-semibold">Feedback Responses</h2>
              <p className="text-gray-600">{selectedFeedback?.title}</p>
            </div>
            {feedbackResponses.length > 0 && (
              <Button
                onClick={handleDownloadResponsesExcel}
                className="bg-green-600 hover:bg-green-700 flex items-center space-x-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Download Excel</span>
              </Button>
            )}
          </div>
        </ModalHeader>

        <ModalBody className="space-y-4 overflow-y-auto max-h-[70vh]">
          {responsesLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <p className="mt-4 text-gray-600">Loading responses...</p>
            </div>
          ) : feedbackResponses.length > 0 ? (
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">
                Total Responses: {feedbackResponses.length}
              </h4>
              {feedbackResponses.map((response, index) => (
                <div key={response.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-gray-900">Response #{index + 1}</h5>
                    <span className="text-sm text-gray-500">
                      User ID: {response.user_id}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p><strong>Responses:</strong> {JSON.stringify(response.responses)}</p>
                    {response.created_at && (
                      <p><strong>Submitted:</strong> {new Date(response.created_at).toLocaleString()}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Responses Yet</h3>
              <p>No one has responded to this feedback yet.</p>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            onClick={() => setIsResponseModalOpen(false)}
            className="bg-gray-500 hover:bg-gray-600"
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ClassFeedback;